import { Link } from '@/components/link/Link';
import { getTellersInIds } from '@raysee/firestore';
import type { Schedule } from '@raysee/schema';
import dayjs from 'dayjs';
import Image from 'next/legacy/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';

const Times24 = [...Array(24)].map((_, i) => i);

export default function SchedulePanel({
  schedules,
  targetDate,
}: {
  schedules: Schedule[];
  targetDate: Date;
}) {
  const tellerIds = Array.from(
    new Set(
      schedules
        .sort((a, b) => a.startTime.getDate() - b.startTime.getDate())
        .map((s) => s.tellerId),
    ),
  );

  const { data: tellers } = useSWR(['tellers', ...tellerIds], () => getTellersInIds(tellerIds), {
    revalidateOnMount: true,
    revalidateOnFocus: false,
  });

  const [sordTime, setSordTime] = useState<number>(0);
  useEffect(() => {
    targetDate && setSordTime(0);
  }, [targetDate]);

  const isEnableBook = useCallback(
    (tellerId: string, time: number) => {
      const targetTime = dayjs(targetDate).startOf('day').add(time, 'hour').add(1, 'minute');

      return schedules.some(
        (s) =>
          s.tellerId === tellerId &&
          dayjs(targetTime).isAfter(dayjs(s.startTime).startOf('hour')) &&
          dayjs(targetTime).isSameOrBefore(dayjs(s.endTime).endOf('hour')),
      );
    },
    [schedules, targetDate],
  );

  const sortedTellers = useMemo(() => {
    return (
      tellers?.sort(
        (a, b) => (isEnableBook(b.id, sordTime) ? 1 : 0) - (isEnableBook(a.id, sordTime) ? 1 : 0),
      ) ?? []
    );
  }, [tellers, isEnableBook, sordTime]);

  return (
    <div className="whitespace-nowrap overflow-auto h-[600px] w-[100%]">
      <table className="table-auto">
        <thead className="text-gray-700 bg-gray-100 sticky top-0 z-10">
          <tr>
            <th className="sticky left-0 h-10 bg-gray-50 border-l border-t border-b-gray-500 " />
            <th className="bg-gray-50 border-t border-b-gray-500 border-r-gray-500" />
            {Times24.map((i) => (
              <th
                key={i}
                className="min-w-[48px] border border-gray-200 border-b-gray-500 cursor-pointer"
                onClick={() => {
                  setSordTime(i);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSordTime(i);
                  }
                }}
              >
                {i}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedTellers?.map((teller) => (
            <tr key={teller.id} className="border-b border-gray-200 dark:border-gray-700">
              <th
                scope="row"
                className="sticky left-0 truncate px-2 max-w-[70px] bg-gray-50 border-l border-t border-b "
              >
                <Link href={`/tellers/${teller.id}`} legacyBehavior>
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <div className="items-center justify-center">
                      <div
                        className="m-auto rounded-full border-[3px]"
                        style={{ width: 56, height: 56 }}
                      >
                        <Image
                          src={teller.imageUrl}
                          alt={teller.name}
                          className="rounded-full"
                          width={50}
                          height={50}
                        />
                      </div>
                    </div>
                  </div>
                </Link>
              </th>
              <td
                scope="row"
                className=" px-2 max-w-[120px] bg-gray-50 border-t border-b  border-r-gray-500"
              >
                <Link href={`/tellers/${teller.id}`} legacyBehavior>
                  <div className="flex items-center space-x-2 cursor-pointer">
                    <p className="truncate  font-bold ">{teller.name}先生</p>
                  </div>
                </Link>
              </td>
              {Times24.map((i) => (
                <td key={i} className="border border-gray-200">
                  {isEnableBook(teller.id, i) ? (
                    <Link href={`/tellers/${teller.id}`} legacyBehavior>
                      <div className="cursor-pointer ">
                        <p className="text-phone-primary text-3xl text-center">●</p>
                      </div>
                    </Link>
                  ) : (
                    <></>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
